import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDangerous = false
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
             position: 'fixed',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             background: 'rgba(0,0,0,0.75)',
             zIndex: 2000,
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             backdropFilter: 'blur(8px)',
             WebkitBackdropFilter: 'blur(8px)'
        }}
      >
        <motion.div
          className="glass-card"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
              padding: 'var(--spacing-2xl)',
              maxWidth: '430px',
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
              position: 'relative',
              boxShadow: 'var(--shadow-xl)',
              background: 'var(--bg-darker)',
              border: '1px solid var(--glass-border)'
          }}
        >
          <button 
             onClick={onClose}
             className="close-btn"
             style={{
                 position: 'absolute',
                 top: '1.25rem',
                 right: '1.25rem',
                 background: 'none',
                 border: 'none',
                 color: 'var(--text-muted)',
                 cursor: 'pointer',
                 padding: '0.5rem',
                 borderRadius: '50%',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 transition: 'all 0.2s'
             }}
          >
             <X size={20} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ 
                  background: isDangerous ? 'var(--error-bg)' : 'var(--warning-bg)',
                  padding: '1rem',
                  borderRadius: '16px',
                  color: isDangerous ? 'var(--error)' : 'var(--warning)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
              }}>
                  <AlertTriangle size={28} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>{title}</h3>
          </div>
          
          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '1rem', margin: 0 }}>{message}</p>
          
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
             <button className="btn btn-ghost" onClick={onClose}>
                 {cancelText}
             </button>
             <button 
                  className="btn"
                  style={{ 
                      background: isDangerous ? 'var(--error)' : 'var(--gradient-primary)',
                      color: 'white',
                      border: 'none',
                      boxShadow: isDangerous ? '0 8px 20px rgba(255, 71, 87, 0.25)' : 'var(--shadow-glow)'
                  }}
                  onClick={() => {
                      onConfirm();
                      onClose();
                  }}
             >
                  {confirmText}
             </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConfirmationModal;
